import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import { useQuery } from "@tanstack/react-query";

import { clockTime, timeLeft } from "../../lib/format";
import { overviewQueryOptions, SellerQueryError } from "../../lib/queries/seller";
import { useSellerStore } from "../../lib/store/seller-store";
import { useNow } from "../../lib/use-now";
import { SellerErrorState } from "../seller/seller-error-state";
import { ThreadView } from "../seller/thread-view";
import { BarsIcon, CloseIcon, HourglassIcon } from "./icons";
import { funnelHeadline, ProjectFunnel } from "./project-funnel";

// The open project's inbox, sliding over the page from the right: the conversation with
// Mira. Any press outside it (the scrim covering the rest of the page) closes it, as do
// Escape and its close button. Where the whole project stands (the funnel) stays out of the
// way until the talent asks for it with the button in the inbox's own bar. The project's
// phase is not repeated here: the card already shows it. Server state only (the overview
// and the thread detail, keyed by talent + thread); which extra the talent opened is view
// state.
export function ProjectPanel({ threadId }: { threadId: string }) {
  const sellerId = useSellerStore((s) => s.activeSellerId);
  const setSelectedThread = useSellerStore((s) => s.setSelectedThread);
  const q = useQuery(overviewQueryOptions(sellerId, threadId));
  const [standsOpen, setStandsOpen] = useState(false);
  const panelRef = useRef<HTMLElement | null>(null);
  const close = () => setSelectedThread(null);

  // Focus moves into the inbox as it opens on a project, so a keyboard user lands in it.
  useEffect(() => {
    panelRef.current?.focus();
  }, [threadId]);

  function onKeyDown(e: KeyboardEvent<HTMLElement>): void {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  }

  const o = q.data;
  const name = o ? o.project_name || "Untitled project" : null;
  const settled = o ? o.stage === "declined" || o.stage === "closed" : false;

  return (
    <div className="sl-inbox" role="presentation">
      <button
        type="button"
        className="sl-inbox__scrim"
        aria-label="Close the inbox"
        tabIndex={-1}
        onClick={close}
      />
      <aside
        id="sl-project-panel"
        ref={panelRef}
        className="sl-panel"
        data-stands={standsOpen}
        role="dialog"
        aria-modal="true"
        aria-label={name ? `${name}: inbox` : "Project inbox"}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="sl-panel__head">
          <div className="sl-panel__heading">
            <span className="sl-panel__eyebrow">Inbox</span>
            {name ? (
              <h2 className="sl-panel__title">{name}</h2>
            ) : (
              <span className="sl-skel sl-skel--line" style={{ width: "60%" }} />
            )}
          </div>
          {o?.proposals_close_at ? <ReviewTimer closesAt={o.proposals_close_at} /> : null}
          <button
            type="button"
            className="sl-panel__toggle"
            aria-pressed={standsOpen}
            aria-controls="sl-stands"
            onClick={() => setStandsOpen(!standsOpen)}
          >
            <BarsIcon size={15} />
            Where it stands
          </button>
          <button
            type="button"
            className="sl-icon-btn"
            onClick={close}
            aria-label="Close the inbox"
            title="Close"
          >
            <CloseIcon size={16} />
          </button>
        </header>
        <div className="sl-panel__body">
          <div className="sl-panel__chat">
            <ThreadView threadId={threadId} />
          </div>
          {standsOpen ? (
            <section id="sl-stands" className="sl-stands" aria-labelledby="sl-stands-title">
              <h3 id="sl-stands-title" className="sl-stands__title">
                <BarsIcon size={15} />
                Where this project stands
              </h3>
              {q.isPending ? (
                <span className="sl-skel sl-skel--card" />
              ) : q.isError ? (
                <SellerErrorState
                  title="Couldn't load this project"
                  message={
                    q.error instanceof SellerQueryError ? q.error.message : "Something went wrong."
                  }
                  onRetry={() => void q.refetch()}
                />
              ) : (
                <>
                  <p className="sl-stands__headline">
                    {funnelHeadline(q.data.funnel, q.data.phase)}
                  </p>
                  <ProjectFunnel funnel={q.data.funnel} you={q.data.you} settled={settled} />
                </>
              )}
            </section>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

/** The project's timer: how long until the client reviews the proposals (the run's
 *  collection deadline). Ticks on the talent's clock; at zero it says the review is on,
 *  until the next read of the project replaces it with the new phase. */
function ReviewTimer({ closesAt }: { closesAt: string }) {
  const now = useNow(15_000);
  const end = Date.parse(closesAt);
  if (!Number.isFinite(end)) return null;
  const left = timeLeft(end - now);
  if (!left) {
    return (
      <span className="sl-timer sl-tone--muted" role="status">
        <HourglassIcon size={14} />
        The client is reviewing proposals
      </span>
    );
  }
  const soon = end - now < 60 * 60_000;
  return (
    <span
      className={`sl-timer sl-tone--${soon ? "wait" : "ai"}`}
      role="timer"
      title={`The client reviews proposals at ${clockTime(closesAt)}, your time.`}
    >
      <HourglassIcon size={14} />
      <span>
        Client reviews proposals in <strong>{left}</strong>
      </span>
    </span>
  );
}
